import { useEffect } from 'react'
import {
  TypeMediaReleaseArticlePageTemplateWithoutUnresolvableLinksResponse,
  TypeRichTextWithoutUnresolvableLinksResponse,
  TypeSeoTagsWithoutUnresolvableLinksResponse,
} from 'contentful-types'
import Palette from '@telus-uds/palette-allium/build/web/palette'

import { Locale } from '../locale'
import Head from './Head/Head'
import Legal from './Legal'

import { adobePageAnalytics } from '../utils/adobeAnalyticsObject'
import dynamic from 'next/dynamic'
import { BlockRendererProps } from '../renderer/contentful/BlockRenderer'
import {
  Box,
  Card,
  ChevronLink,
  Divider,
  ExpandCollapse,
  FlexGrid,
  Paragraph,
  Spacer,
  Typography,
} from '@telus-uds/components-web'

import { localizeDateFormat } from 'src/utils/localizeDateFormat'

import { ARTICLE_PAGE_I18N } from './ArticlePage.i18n'
import { ISiteBuilderEntry } from '../utils/types/ISiteBuilderEntryTypes'
import { getArticleSnapshots } from './utils/getArticleSnapshots'
import { getArticleContacts } from './utils/getArticleContacts'

const BlockRenderer = dynamic<BlockRendererProps>(() =>
  import('../renderer/contentful/BlockRenderer').then((mod) => mod.BlockRenderer)
)
declare global {
  interface Window {
    dataLayer: any
    adobeDataLayer: any
  }
}

export type ArticlePageProps = {
  articlePageTemplate: ISiteBuilderEntry<TypeMediaReleaseArticlePageTemplateWithoutUnresolvableLinksResponse['fields']>
  locale: Locale
  articleBackLinkCta?: string
  articleBackLinkURL?: string
  brand?: 'telus'
}

const renderRichText = (richText: TypeRichTextWithoutUnresolvableLinksResponse) => (
  <BlockRenderer block={richText} fieldPath={'richText'} />
)

export function ArticlePage({ articlePageTemplate, locale, articleBackLinkCta, articleBackLinkURL }: ArticlePageProps) {
  const { seo, slug } = articlePageTemplate.fields
  const { language } = locale
  // This is needed as adobePageAnalytics checks if the invalid region landing page is used on generic page template
  const incorrectRegion = false
  useEffect(() => {
    adobePageAnalytics(articlePageTemplate.fields, locale, incorrectRegion)
  })

  const landAcknowledgmentExist = !!articlePageTemplate.fields.landAcknowledgment

  if (slug.includes('about/news-and-events/media-releases/')) {
    articleBackLinkCta = ARTICLE_PAGE_I18N[language].BACK_TO_ALL_STORIES
    articleBackLinkURL = 'https://www.telus.com/about/media-hub'
  }
  if (slug.includes('health/press-releases/')) {
    articleBackLinkCta = ARTICLE_PAGE_I18N[language].RETURN_TO_PRESS_RELEASES
    articleBackLinkURL = 'https://www.telus.com/health/press-releases'
  }

  return (
    <>
      <Head
        title={(seo?.fields as TypeSeoTagsWithoutUnresolvableLinksResponse['fields'])?.title}
        language={language}
        seo={seo?.fields as unknown as TypeSeoTagsWithoutUnresolvableLinksResponse['fields']}
      />
      <main id="app" data-sb-object-id={articlePageTemplate.sys.id}>
        <Spacer space={4} />

        <FlexGrid>
          {articleBackLinkCta && articleBackLinkURL && (
            <FlexGrid.Row>
              <FlexGrid.Col>
                <ChevronLink direction="left" href={articleBackLinkURL}>
                  {articleBackLinkCta}
                </ChevronLink>
              </FlexGrid.Col>
            </FlexGrid.Row>
          )}

          <Spacer space={3} />

          <FlexGrid.Row>
            <FlexGrid.Col
              xs={12}
              sm={12}
              md={12}
              lg={landAcknowledgmentExist ? 6 : 8}
              xl={landAcknowledgmentExist ? 6 : 8}
              lgOffset={2}
              xlOffset={2}
            >
              <Typography variant={{ size: 'eyebrow' }} tokens={{ color: Palette.color.greyShuttle }}>
                {localizeDateFormat(articlePageTemplate.fields.publishDate, language).toUpperCase()}
              </Typography>
              <Spacer space={3} />
              <Typography variant={{ size: 'h1' }} heading="h1">
                {articlePageTemplate.fields.heading}
              </Typography>
              <Spacer space={3} />
              <Typography variant={{ size: 'h3', colour: 'secondary' }} heading="h3">
                {articlePageTemplate.fields.subheading}
              </Typography>
              <Spacer space={3} />
            </FlexGrid.Col>

            {landAcknowledgmentExist && (
              <FlexGrid.Col xs={12} sm={12} md={12} lg={3} xl={3} lgOffset={1} xlOffset={1}>
                <Spacer space={{ xs: 5, md: 3 }} />
                <Card variant={{ background: 'alternative', padding: 'compact' }}>
                  <Typography variant={{ size: 'h4' }} heading="h4">
                    {ARTICLE_PAGE_I18N[language].LAND_ACKNOWLEDGMENT}
                  </Typography>
                  <Box>
                    <Paragraph variant={{ size: 'small' }}>
                      {articlePageTemplate.fields.landAcknowledgment.fields.content}
                    </Paragraph>
                  </Box>
                </Card>
              </FlexGrid.Col>
            )}
          </FlexGrid.Row>

          {articlePageTemplate.fields.snapshots && (
            <>
              <Spacer space={5} />

              {articlePageTemplate.fields.snapshotsHeading && (
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                    <Typography variant={{ size: 'h4' }} tokens={{ color: Palette.color.greyThunder }} heading="h4">
                      {articlePageTemplate.fields.snapshotsHeading}
                    </Typography>
                  </FlexGrid.Col>
                </FlexGrid.Row>
              )}

              <Spacer space={1} />

              <FlexGrid.Row>{getArticleSnapshots(articlePageTemplate.fields.snapshots)}</FlexGrid.Row>
            </>
          )}

          {articlePageTemplate.fields.emphasis && (
            <>
              <Spacer space={4} />

              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                  <Paragraph variant={{ size: 'large' }}>{articlePageTemplate.fields.emphasis}</Paragraph>
                </FlexGrid.Col>
              </FlexGrid.Row>
            </>
          )}

          <Spacer space={6} />

          <FlexGrid.Row>
            <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2} testID="content-col">
              {renderRichText(articlePageTemplate.fields.content)}
            </FlexGrid.Col>
          </FlexGrid.Row>

          <Spacer space={6} />

          {articlePageTemplate.fields.aboutPartnerCompanyHeading &&
            articlePageTemplate.fields.aboutPartnerCompanyContent && (
              <>
                <FlexGrid.Row>
                  <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                    <Divider />
                  </FlexGrid.Col>
                </FlexGrid.Row>

                <Spacer space={6} />

                <FlexGrid.Row>
                  <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                    <Typography variant={{ size: 'h4' }} heading="h4">
                      {articlePageTemplate.fields.aboutPartnerCompanyHeading}
                    </Typography>
                    <Spacer space={3} />
                    {renderRichText(articlePageTemplate.fields.aboutPartnerCompanyContent)}
                  </FlexGrid.Col>
                </FlexGrid.Row>

                <Spacer space={6} />
              </>
            )}

          {articlePageTemplate.fields.aboutTelus && (
            <>
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                  <Divider />
                </FlexGrid.Col>
              </FlexGrid.Row>

              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                  <ExpandCollapse>
                    {(expandProps) => (
                      <ExpandCollapse.Panel
                        panelId="main"
                        {...expandProps}
                        control={
                          <Typography variant={{ size: 'h4' }}>{ARTICLE_PAGE_I18N[language].ABOUT_TELUS}</Typography>
                        }
                      >
                        {renderRichText(articlePageTemplate.fields.aboutTelus)}
                      </ExpandCollapse.Panel>
                    )}
                  </ExpandCollapse>
                </FlexGrid.Col>
              </FlexGrid.Row>
            </>
          )}

          {articlePageTemplate.fields.contacts && (
            <>
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                  <Divider />
                </FlexGrid.Col>
              </FlexGrid.Row>

              <Spacer space={8} />
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} sm={12} md={12} lg={8} xl={8} lgOffset={2} xlOffset={2}>
                  <Typography variant={{ size: 'h4' }} heading="h4">
                    {ARTICLE_PAGE_I18N[language].CONTACTS}
                  </Typography>
                  <Spacer space={3} />
                </FlexGrid.Col>
              </FlexGrid.Row>

              <FlexGrid.Row>{getArticleContacts(articlePageTemplate.fields.contacts)}</FlexGrid.Row>
            </>
          )}

          <Spacer space={10} />
        </FlexGrid>
        <Legal />
      </main>
    </>
  )
}
